"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider

// Context to pass touch handlers from Tooltip root to TooltipTrigger
const TooltipTouchContext = React.createContext(null)

const Tooltip = ({ children, delayDuration = 400, ...props }) => {
  const [open, setOpen] = React.useState(false)
  const touchActiveRef = React.useRef(false)
  const timerRef = React.useRef(null)

  const touchHandlers = React.useMemo(() => ({
    onTouchStart: () => {
      touchActiveRef.current = true
      if (timerRef.current) clearTimeout(timerRef.current)
      setOpen(true)
    },
    onTouchEnd: () => {
      timerRef.current = setTimeout(() => {
        touchActiveRef.current = false
        setOpen(false)
      }, 1500)
    },
  }), [])

  const handleOpenChange = React.useCallback((newOpen) => {
    // While touch is active, don't let Radix close the tooltip
    if (!newOpen && touchActiveRef.current) return
    setOpen(newOpen)
  }, [])

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return (
    <TooltipTouchContext.Provider value={touchHandlers}>
      <TooltipPrimitive.Root
        open={open}
        onOpenChange={handleOpenChange}
        delayDuration={delayDuration}
        {...props}
      >
        {children}
      </TooltipPrimitive.Root>
    </TooltipTouchContext.Provider>
  )
}

const TooltipTrigger = React.forwardRef<any, any>(({ ...props }, ref) => {
  const touchHandlers = React.useContext(TooltipTouchContext)

  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      {...props}
      onTouchStart={(e) => {
        touchHandlers?.onTouchStart()
        props.onTouchStart?.(e)
      }}
      onTouchEnd={(e) => {
        touchHandlers?.onTouchEnd()
        props.onTouchEnd?.(e)
      }}
    />
  )
})
TooltipTrigger.displayName = "TooltipTrigger"

const TooltipContent = React.forwardRef<any, any>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md bg-white border border-slate-200 shadow-lg px-3 py-1.5 text-xs text-slate-700 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props} />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
